'use client';

// ESQUEMA de la decisión "Indagación previa (Art. 208)".
// La SELECCIÓN (DDHH, si se determinó al autor, y la decisión archivo/apertura) y el
// TEXTO editado del documento se PERSISTEN en `DecisionInicial.documento_datos`, para que
// el abogado especializado y el Director vean exactamente el documento que aplica.

import { useState } from 'react';
import { Users, FileText } from 'lucide-react';
import type { ExpedienteDetalle as ExpDetalle } from '@/lib/expedientes';
import { guardarDocumentoDecision, type DocumentoBorrador } from '@/lib/decisionFlujo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SiNo, AlertaTermino, Campo, SubForm } from './decisionesUI';
import DocumentoDecisionDialog from './DocumentoDecisionDialog';
import {
  DOC_INDAGACION_PREVIA, DOC_AUTO_APERTURA, type DatosDoc,
} from '@/lib/plantillas/decisiones-docs';

export default function IndagacionPreviaForm({ exp }: { exp: ExpDetalle }) {
  const radicado = exp.radicado_manual || exp.radicado;
  const borr = (exp.decision_inicial?.documento_datos ?? {}) as DocumentoBorrador;
  const sel0 = borr.seleccion ?? {};

  const [fecha, setFecha] = useState(String(sel0.fecha ?? ''));
  const [ddhh, setDdhh] = useState<boolean | null>((sel0.ddhh as boolean | undefined) ?? null);
  const [determinado, setDeterminado] = useState<boolean | null>((sel0.determinado as boolean | undefined) ?? null);
  const [decision, setDecision] = useState(String(sel0.decision ?? ''));
  const [docsData, setDocsData] = useState<Record<string, DatosDoc>>(
    (borr.docs as Record<string, DatosDoc> | undefined) ?? {},
  );
  const [docAbierto, setDocAbierto] = useState(false);

  const cfg = decision === 'archivo' ? DOC_INDAGACION_PREVIA : DOC_AUTO_APERTURA;

  function persistir(patchSel: Record<string, unknown>, nextDocs = docsData) {
    const seleccion = { fecha, ddhh, determinado, decision, ...patchSel };
    void guardarDocumentoDecision(exp.id, { seleccion, docs: nextDocs }).catch(() => {});
  }
  const guardarDoc = async (datos: DatosDoc) => {
    const nextDocs = { ...docsData, [cfg.plantillaId]: datos };
    setDocsData(nextDocs);
    await guardarDocumentoDecision(exp.id, { seleccion: { fecha, ddhh, determinado, decision }, docs: nextDocs });
  };

  return (
    <div className="space-y-4">
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="size-4" /> Cuando no se tenga establecido el funcionario que cometió la conducta.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo label="Radicado">
          <Input value={radicado} readOnly disabled />
        </Campo>
        <Campo label="Fecha de indagación previa" requerido>
          <Input type="date" value={fecha}
            onChange={(e) => { setFecha(e.target.value); persistir({ fecha: e.target.value }); }} />
        </Campo>
      </div>

      <AlertaTermino tono={ddhh ? 'rojo' : 'ambar'}>
        <strong>Alerta de {ddhh ? '12 meses (6 + 6 por DDHH/DIH)' : '6 meses'}</strong> desde la fecha de
        indagación previa para establecer al autor. Si no se logra, <strong>se archiva</strong>.
        {' '}<em className="text-muted-foreground">(El vencimiento se calcula con el motor de términos.)</em>
      </AlertaTermino>

      <SiNo
        label="¿Es una investigación por violación de Derechos Humanos o Derecho Internacional Humanitario?"
        value={ddhh} onChange={(v) => { setDdhh(v); persistir({ ddhh: v }); }}
      >
        <AlertaTermino tono="rojo">La alerta <strong>se extiende 6 meses más</strong>.</AlertaTermino>
      </SiNo>

      <SiNo
        label="¿Se determinó quién cometió la conducta?"
        value={determinado}
        onChange={(v) => {
          setDeterminado(v);
          // Si NO se determinó al autor, no aplica el auto de apertura: se limpia si estaba elegido.
          const nextDecision = !v && decision === 'apertura' ? '' : decision;
          if (nextDecision !== decision) setDecision(nextDecision);
          persistir({ determinado: v, decision: nextDecision });
        }}
      >
        <SubForm titulo="Datos de quien cometió la conducta">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo label="Nombre"><Input /></Campo>
            <Campo label="Cédula"><Input inputMode="numeric" /></Campo>
            <Campo label="Cargo"><Input /></Campo>
            <Campo label="Dependencia"><Input /></Campo>
          </div>
        </SubForm>
      </SiNo>

      <Campo label="Decisión" requerido>
        <Select value={decision} onValueChange={(v) => { setDecision(v); persistir({ decision: v }); }}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="archivo">Archivo definitivo (art. 224)</SelectItem>
            {/* El auto de apertura solo aplica si se determinó al autor (no si respondieron "No"). */}
            {determinado !== false && (
              <SelectItem value="apertura">Auto de apertura de la investigación</SelectItem>
            )}
          </SelectContent>
        </Select>
      </Campo>

      {/* Según la decisión se genera la minuta correspondiente (previsualizar/editar/descargar). */}
      {decision && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm">
              <FileText className="mr-1 inline size-4 text-primary" />
              {decision === 'archivo'
                ? 'Documento: Auto de indagación previa (Art. 208)'
                : 'Documento: Auto de apertura de investigación (Art. 211-213)'}
            </span>
            <Button type="button" size="sm" onClick={() => setDocAbierto(true)}>
              <FileText className="size-3.5" /> Generar / previsualizar
            </Button>
          </div>
        </div>
      )}

      {docAbierto && decision && (
        <DocumentoDecisionDialog
          exp={exp} cfg={cfg}
          guardado={docsData[cfg.plantillaId]} onGuardar={guardarDoc}
          onClose={() => setDocAbierto(false)}
        />
      )}
    </div>
  );
}
